import React, { useState } from 'react';
import { loginWithEmail, registerWithEmail, loginWithGoogle } from '../lib/firebase.js';
import { HamburgerMenu } from './HamburgerMenu';

interface AuthProps {
  onAuthSuccess: (user: any) => void;
  onBackToGuest: () => void;
}

export const Auth: React.FC<AuthProps> = ({ onAuthSuccess, onBackToGuest }) => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError('メールアドレスとパスワードを入力してください。');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      let user;
      if (isSignUp) {
        user = await registerWithEmail(email.trim(), password);
      } else {
        user = await loginWithEmail(email.trim(), password);
      }
      onAuthSuccess(user);
    } catch (err: any) {
      console.error(err);
      if (err.code === 'auth/email-already-in-use') {
        setError('このメールアドレスは既に登録されています。');
      } else if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password' || err.code === 'auth/user-not-found') {
        setError('メールアドレスまたはパスワードが正しくありません。');
      } else if (err.code === 'auth/weak-password') {
        setError('パスワードは6文字以上で入力してください。');
      } else {
        setError(err.message || '認証に失敗しました。');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError(null);
    setLoading(true);
    try {
      const user = await loginWithGoogle();
      onAuthSuccess(user);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Googleログインに失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="theme-stage">
      {/* 画面左上に固定する「戻る」ボタンとハンバーガーメニューのクラスタ */}
      <div className="auth-screen__top-left">
        <button
          type="button"
          className="auth-screen__back"
          onClick={onBackToGuest}
          disabled={loading}
          aria-label="戻る"
        >
          ← 戻る
        </button>
        <HamburgerMenu />
      </div>

      <span className="theme-blob theme-blob--a" aria-hidden="true" />
      <span className="theme-blob theme-blob--b" aria-hidden="true" />
      <span className="theme-blob theme-blob--c" aria-hidden="true" />

      <div className="theme-stage__content">
        <section className="auth-screen" aria-label="ログイン">
          <div className="auth-screen__hero">
            <h1 className="auth-screen__title">
              {isSignUp ? 'アカウント作成' : 'おかえりなさい'}
            </h1>
            <p className="auth-screen__title-accent">
              {isSignUp ? 'sign up' : 'sign in'}
            </p>
            <p className="auth-screen__lede">
              {isSignUp
                ? 'アカウントを作成して、複数の部屋を管理しましょう'
                : 'ログインして管理している部屋に参加します'}
            </p>
          </div>

          {error && <div className="auth-screen__error">{error}</div>}

          <form onSubmit={handleSubmit} className="auth-screen__card">
            <div className="auth-screen__field">
              <label className="auth-screen__label" htmlFor="email">メールアドレス</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@email.com"
                disabled={loading}
                className="auth-screen__input"
                required
              />
            </div>

            <div className="auth-screen__field">
              <label className="auth-screen__label" htmlFor="password">パスワード</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                disabled={loading}
                className="auth-screen__input"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="auth-screen__submit"
            >
              {loading ? '処理中…' : isSignUp ? '登録する' : 'ログインする'}
            </button>
          </form>

          <div className="auth-screen__divider" aria-hidden="true">
            <span className="auth-screen__divider-line" />
            <span>または</span>
            <span className="auth-screen__divider-line" />
          </div>

          <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={loading}
            className="auth-screen__google"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
              <path fill="#4285F4" d="M17.64 9.2c0-.63-.06-1.25-.16-1.84H9v3.47h4.84c-.21 1.12-.84 2.07-1.8 2.72v2.24h2.9c1.7-1.57 2.7-3.88 2.7-6.59z" />
              <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.24c-.8.54-1.84.87-3.06.87-2.35 0-4.34-1.59-5.05-3.73H.96v2.3C2.44 15.96 5.48 18 9 18z" />
              <path fill="#FBBC05" d="M3.95 10.72c-.18-.54-.28-1.12-.28-1.72s.1-1.18.28-1.72V5H.96C.35 6.2 0 7.57 0 9s.35 2.8 1.04 4l2.91-2.28z" />
              <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35L15 2.4C13.46.99 11.43 0 9 0 5.48 0 2.44 2.04.96 5.04l2.99 2.28c.71-2.14 2.7-3.74 5.05-3.74z" />
            </svg>
            Googleで続ける
          </button>

          <div className="auth-screen__toggle">
            <button
              type="button"
              onClick={() => setIsSignUp(!isSignUp)}
              disabled={loading}
              className="auth-screen__link"
            >
              {isSignUp ? 'すでにアカウントをお持ちですか？ ログイン →' : '初めての方は 新規登録 →'}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

